import Joi from 'joi';
import PropTypes from 'prop-types';

import { ANOMALY_CHANCE_OF_OCCURRENCE_OPTIONS, ANOMALY_PEOPLE_AT_RISK_OPTIONS, RISK_RATING_OPTIONS } from '../../../utils/constants';

import Button from '../../themed/button';
import Form from '../../common/form';

import toastService from '../../../services/toastService';

class RiskAssessmentItemEdit extends Form {
	state = {
		data: {
			structuralMember: '',
			quantity: '',
			hazardDescription: '',
			peopleAtRisk: [],
			chanceOfOccurrence: '',
			hazardConsequence: '',
			riskRating: '',
			controlsRequired: '',
			controlledHazardConsequence: '',
			controlledChanceOfOccurrence: '',
			residual: '',
			ref: '',
		},
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		structuralMember: Joi.string().min(3).required().label('Structural Member'),
		quantity: Joi.number().positive().required().label('Quantity'),
		hazardDescription: Joi.string().min(3).required().label('Hazard Description and Effect'),
		peopleAtRisk: Joi.array().min(1).items(
			Joi.object({
				label: Joi.string().required(),
				value: Joi.string().required(),
				__isNew__: Joi.boolean().optional(),
			})
		).required().label('People or Property at Risk'),
		chanceOfOccurrence: Joi.string().valid(...ANOMALY_CHANCE_OF_OCCURRENCE_OPTIONS).required().label('Chance of Occurrence'),
		hazardConsequence: Joi.string().valid(...RISK_RATING_OPTIONS).min(3).required().label('Hazard Consequence'),
		riskRating: Joi.string().valid(...RISK_RATING_OPTIONS).min(3).required().label('Risk Rating'),
		controlsRequired: Joi.string().min(3).required().label('Controls Required'),
		controlledHazardConsequence: Joi.string().valid(...RISK_RATING_OPTIONS).min(3).required().label('Controlled Hazard Consequence'),
		controlledChanceOfOccurrence: Joi.string().valid(...ANOMALY_CHANCE_OF_OCCURRENCE_OPTIONS).required().label('Controlled Chance of Occurrence'),
		residual: Joi.string().valid(...RISK_RATING_OPTIONS).min(1).required().label('Residual'),
		ref: Joi.string().min(1).required().label('Ref'),
	};

	static propTypes = {
		initialData: PropTypes.object,
		onCancelEdit: PropTypes.func,
		onEdit: PropTypes.func,
	};

	componentDidMount() {
		if (this.props.initialData) {
			const initialData = { ...this.props.initialData };
			if (!initialData.quantity) initialData.quantity = '';
			
			this.setState({ data: this.props.initialData });
		}
	}

	doSubmit = (): void => {
		this.props.onEdit(this.state.data);
		toastService.success('Assessment Item Edited Successfully!');
	};

	render() {
		return (
			<form autoComplete="off" onSubmit={this.handleSubmit}>
				<div className="row">
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'structuralMember',
							placeholder: 'Enter Structural Member',
							label: 'Structural Member',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'quantity',
							placeholder: 'Enter Quantity',
							label: 'Quantity',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'hazardDescription',
							placeholder: 'Enter Hazard Description',
							label: 'Hazard Description',
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderCreatableMultiselect({
							closeMenuOnSelect: true,
							options: ANOMALY_PEOPLE_AT_RISK_OPTIONS.map((option) => ({ label: option, value: option })),
							name: 'peopleAtRisk',
							placeholder: 'Enter People at Risk',
							label: 'People at Risk',
							isMulti: true,
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'chanceOfOccurrence',
							placeholder: 'Select Chance of Occurrence',
							label: 'Chance of Occurrence',
							options: ANOMALY_CHANCE_OF_OCCURRENCE_OPTIONS.map((option) => ({ id: option, name: option })),
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'riskRating',
							placeholder: 'Select Risk Rating',
							label: 'Risk Rating',
							options: RISK_RATING_OPTIONS.map((option) => ({ id: option, name: option })),
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							name: 'hazardConsequence',
							placeholder: 'Enter Hazard Consequence',
							label: 'Hazard Consequence',
							options: RISK_RATING_OPTIONS.map((option) => ({ id: option, name: option })),
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'controlsRequired',
							placeholder: 'Enter Controls Required',
							label: 'Controls Required',
						})}
					</div>
					
					<div className="col">
						{this.renderSelect({
							name: 'controlledHazardConsequence',
							placeholder: 'Enter Controlled Hazard Consequence',
							label: 'Controlled Hazard Consequence',
							options: RISK_RATING_OPTIONS.map((option) => ({ id: option, name: option })),
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'controlledChanceOfOccurrence',
							placeholder: 'Select Controlled Chance of Occurrence',
							label: 'Controlled Chance of Occurrence',
							options: ANOMALY_CHANCE_OF_OCCURRENCE_OPTIONS.map((option) => ({ id: option, name: option })),
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							name: 'residual',
							placeholder: 'Enter Residual',
							label: 'Residual',
							options: RISK_RATING_OPTIONS.map((option) => ({ id: option, name: option })),
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							name: 'ref',
							placeholder: 'Enter Ref',
							label: 'Ref',
						})}
					</div>
				</div>
				
				<div className="divider mt-4"></div>

				<div className="d-flex justify-content-end">
					{this.renderButton('Save Changes')}

					<div className="ms-3">
						<Button label='Cancel' onClick={this.props.onCancelEdit} style={{ backgroundColor: 'red' }} />
					</div>
				</div>
			</form>
		);
	}
}

export default RiskAssessmentItemEdit;
